import { Box, Tooltip } from '@mui/material';
import UPButton from 'components/button';
import UPDateRangePicker from 'components/date-range-picker/UPDateRangePicker';
import UPDatepicker from 'components/datepicker';
import { UPDropdown } from 'components/dropdown';
import ErrorBar from 'components/error-bar';
import { UPTable } from 'components/table';
import UPWeekPicker from 'components/week-picker';
import { CompanyRole } from 'constants/CompanyRole';
import { ReportType } from 'constants/ReportType';
import useReportingFilters from 'hooks/reporting/useReportingFilters';
import { t } from 'i18next';
import { toNumber } from 'lodash';
import { SectionContainer } from 'modules/companies/styles';
import { getCurrentTime, getISO8601WeekNumber, getISO8601Year } from 'modules/contracts/helpers';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { getCompaniesAsDropdownItemsAction } from 'store/actions/company-actions';
import { getCostCentersDropdownItemsAction } from 'store/actions/company-cost-centers-actions';
import { getCountriesNamesAction } from 'store/actions/countries-actions';
import { getEmployeesDropdownItems } from 'store/actions/employee-actions';
import { getParitairComiteSelectItemsAction } from 'store/actions/paritair-comite-actions';
import {
  clearReportsAction,
  downloadReportAction,
  getDocumentFollowUpAction,
  getReportsAction,
  getReportsErrorAction,
} from 'store/actions/reporting-actions';
import { getSuperAdminsAction } from 'store/actions/superadmins-actions';
import { profileSelector } from 'store/selectors/profile-selectors';
import { reportDataSelector, reportErrorSelector } from 'store/selectors/reporting-selector';
import {
  COST_ESTIMATION__TOOLTIP,
  DOCUMENTS__DATE,
  GENERAL__FROM,
  GENERAL__SHOW,
  GENERAL__UNTIL,
  HEADER__WEEK,
  REPORTING__DOCUMENT_FOLLOW_UP_CATEGORY_REQUIRED,
  REPORTING__DOCUMENT_FOLLOW_UP_COMPANY_REQUIRED,
  REPORTING__DOCUMENT_FOLLOW_UP_DATE_REQUIRED,
  REPORTING__DOCUMENT_FOLLOW_UP_MAX_COMPANIES,
  REPORTING__GENERATE_FAILED,
} from 'translations/constants';
import DocumentFollowUpFilter, { DocumentFollowUpEmployee } from 'types/reporting/DocumentFollowUpFilter';
import ReportingFilter from 'types/reporting/ReportingFilter';
import { ReactComponent as InfoIcon } from '../../assets/icons/InfoIcon.svg';
import BaseReportingFilters from './filters/BaseReportingFilters';
import ClientPerformanceFilters from './filters/ClientPerformanceFilters';
import DocumentFollowUpFilters from './filters/DocumentFollowUpFilters';
import FTECheckFilters from './filters/FTECheckFilters';
import GeneralStatisticsFilters from './filters/GeneralStatisticsFilters';
import StatisticsPerCostCenterFilters from './filters/StatisticsPerCostCenterFilters';
import {
  getReportTypesDropdownValues,
  isReportWithInterval,
  mapDataToClientPerformanceReport,
  mapDataToDocumentFollowUpReport,
  parseDocumentFollowUpEmployeeValue,
  mapDataToFTECheckReport,
  mapDataToRequiredActionsReportData,
  mapDataToStatisticsPerCostCenterReport,
  mapDataToWeeklyReportData,
  MAX_DOCUMENT_FOLLOW_UP_COMPANIES,
} from './helpers';
import ClientPerformanceReport from './reports/ClientPerformance/ClientPerformanceReport';
import DocumentFollowUpReport from './reports/DocumentFollowUpReport';
import FTECheckReport from './reports/FTECheckReport';
import RequiredActionsReport from './reports/RequiredActionsReport';
import { ReactComponent as DownloadIcon } from '../../assets/icons/DownloadGreen.svg';
import StatisticsPerCostCenterReport from './reports/StatisticsPerCostCenterReport';
import WeeklyContractCountReport from './reports/WeeklyContractCountReport';
import { ExportReportButton, FilterItem } from './styles';

const Reporting = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const reportData = useAppSelector(reportDataSelector);
  const [year, setYear] = useState<number | null>(() => getISO8601Year(new Date()));
  const [week, setWeek] = useState<Date | null>(getCurrentTime().date);
  const currentWeek = getISO8601WeekNumber(week);
  const [dateRange, setDateRange] = useState<[Date, Date]>([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const [weekRange, setWeekRange] = useState<[Date, Date]>([week, week]);
  const [yearRange, setYearRange] = useState<[number, number]>([year, year]);
  const startWeek = getISO8601WeekNumber(weekRange[0]);
  const endWeek = getISO8601WeekNumber(weekRange[1]);
  const profile = useAppSelector(profileSelector);
  const isAdmin = profile.role === CompanyRole.ADMIN;
  const userCompany = profile.companyId;
  const allowedRoles = [CompanyRole.ADMIN, CompanyRole.HR_MANAGER, CompanyRole.COMPANY_MANAGER];
  const [reportType, setReportType] = useState<ReportType>(
    isAdmin ? ReportType.WEEKLY_CONTRACT_COUNT : ReportType.STATISTICS_PER_COST_CENTER,
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [referenceDate, setReferenceDate] = useState<Date>(new Date());
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [documentFollowUpGenerated, setDocumentFollowUpGenerated] = useState<boolean>(false);
  const reportError = useAppSelector(reportErrorSelector);

  if (!isAdmin && !profile?.featureWizardEnabled) {
    window.location.href = '/';
  }

  const { dropdownData, reportingFilters, handleFilterChange } = useReportingFilters();

  const {
    selectedCompany,
    selectedCompanies,
    selectedDocumentCategory,
    selectedParitairComites,
    selectedSuperAdmins,
    selectedEmployees,
    selectedWorkerClasses,
    selectedStatus,
    companyName,
    selectedNationalities,
    selectedBirthCountries,
    selectedLanguages,
    employeeName,
    ageRange,
    experienceRange,
    confirmedOnly,
    noShowOnly,
    selectedYear,
    selectedMonth,
    selectedHourType,
    selectedCostCenters,
  } = reportingFilters;

  const formatDate = (date: Date): string => {
    const tzoffset = new Date(date).getTimezoneOffset() * 60000; // offset in milliseconds
    const withOffSet = new Date(date).getTime() - tzoffset;
    const d = new Date(withOffSet).toISOString().split('T');
    return d[0];
  };

  const generateFiltersObject = (): ReportingFilter => {
    let companyIds: number[] = [];

    if (!isAdmin && userCompany) {
      companyIds = [+userCompany];
    } else if (isAdmin && selectedCompany !== '') {
      companyIds = [+selectedCompany];
    } else {
      companyIds = selectedCompanies.map(Number);
    }

    return {
      ReportType: toNumber(reportType),
      Year: reportType === ReportType.CLIENT_PERFORMANCE ? selectedYear : year, // selected year from type 4 reporting
      Week: currentWeek,
      FromWeek: isReportWithInterval(reportType) ? getISO8601WeekNumber(weekRange[0]) : undefined,
      ToWeek: isReportWithInterval(reportType) ? getISO8601WeekNumber(weekRange[1]) : undefined,
      FromYear: isReportWithInterval(reportType) ? yearRange[0] : undefined,
      ToYear: isReportWithInterval(reportType) ? yearRange[1] : undefined,
      FromDate: formatDate(startDate),
      ToDate: formatDate(endDate),
      CompanyName: companyName,
      CompanyIds: companyIds,
      EmployeeName: employeeName,
      ContractStatus: '',
      WorkerClass: selectedWorkerClasses,
      NoShow: noShowOnly,
      Confirmed: confirmedOnly,
      EmployeeNationality: '',
      EmployeeNationalityId: selectedNationalities.map(Number),
      EmployeeLanguage: '',
      EmployeeLanguageId: selectedLanguages.map(Number),
      EmployeeBirthCountryId: selectedBirthCountries.map(Number),
      Location: '',
      CostCenterIds: selectedCostCenters.map(Number),
      CostCenter: '',
      Function: '',
      ParitaireComite: '',
      EmployeeYearsOfExperience: experienceRange,
      EmployeeAge: ageRange,
      EmployeeIds: selectedEmployees.map(Number),
      ParitaireComiteId: selectedParitairComites.map(Number),
      PayrollOfficer: selectedSuperAdmins.map(Number),
      Status: selectedStatus,
      Month: selectedMonth,
      HoursType: selectedHourType,
    };
  };

  const isValidReferenceDate = !!referenceDate && !Number.isNaN(referenceDate.getTime());

  const generateDocumentFollowUpFilter = (): DocumentFollowUpFilter => ({
    CompanyIds: selectedCompanies.map(Number),
    Category: toNumber(selectedDocumentCategory),
    ReferenceDate: formatDate(referenceDate),
    Employees: selectedEmployees
      .map(parseDocumentFollowUpEmployeeValue)
      .filter((employee): employee is DocumentFollowUpEmployee => employee !== null),
  });

  const validateDocumentFollowUpFilters = (): string => {
    if (selectedCompanies.length === 0) {
      return t(REPORTING__DOCUMENT_FOLLOW_UP_COMPANY_REQUIRED);
    }

    if (selectedCompanies.length > MAX_DOCUMENT_FOLLOW_UP_COMPANIES) {
      return t(REPORTING__DOCUMENT_FOLLOW_UP_MAX_COMPANIES);
    }

    if (!selectedDocumentCategory) {
      return t(REPORTING__DOCUMENT_FOLLOW_UP_CATEGORY_REQUIRED);
    }

    if (!isValidReferenceDate) {
      return t(REPORTING__DOCUMENT_FOLLOW_UP_DATE_REQUIRED);
    }

    return '';
  };

  useEffect(() => {
    dispatch(getParitairComiteSelectItemsAction());
    dispatch(getCompaniesAsDropdownItemsAction());
    dispatch(getCountriesNamesAction('en')); // defult language
    dispatch(getSuperAdminsAction());
    if (selectedCompany !== '') {
      dispatch(getCostCentersDropdownItemsAction({ companyId: +selectedCompany }));
      if (reportType !== ReportType.DOCUMENT_FOLLOW_UP) {
        dispatch(getEmployeesDropdownItems(+selectedCompany));
      }
    }
  }, [dispatch, reportType, selectedCompany]);

  const validFilters = (filters: ReportingFilter): boolean => {
    // mock the validations here!
    if (reportType === ReportType.CLIENT_PERFORMANCE && (!filters.Year || !filters.Month)) {
      return false;
    }
    return true;
  };

  const renderData = () => {
    if (reportType === ReportType.DOCUMENT_FOLLOW_UP) {
      const message = validateDocumentFollowUpFilters();
      setValidationMessage(message);

      if (message) {
        dispatch(getReportsErrorAction(false));
        return;
      }

      setDocumentFollowUpGenerated(false);
      dispatch(getDocumentFollowUpAction(generateDocumentFollowUpFilter())).then(() => {
        setDocumentFollowUpGenerated(true);
      });
      return;
    }

    const filters = generateFiltersObject();
    if (validFilters(filters)) dispatch(getReportsAction(filters));
  };

  useEffect(() => {
    dispatch(clearReportsAction());
  }, [dispatch]);

  const onWeekPickerChange = (value: Date) => {
    setWeek(value);
    setYear(getISO8601Year(new Date(value)));
  };

  const onStartWeekPickerChange = (value: Date) => {
    if (weekRange[1] < value) {
      setWeekRange([value, value]);
      setYearRange([new Date(value).getFullYear(), new Date(value).getFullYear()]);
      return;
    }
    setWeekRange([value, weekRange[1]]);
    setYearRange([new Date(value).getFullYear(), new Date(weekRange[1]).getFullYear()]);
  };

  const onEndWeekPickerChange = (value: Date) => {
    if (weekRange[0] > value) {
      setWeekRange([value, value]);
      setYearRange([new Date(value).getFullYear(), new Date(value).getFullYear()]);
      return;
    }
    setWeekRange([weekRange[0], value]);
    setYearRange([new Date(weekRange[0]).getFullYear(), new Date(value).getFullYear()]);
  };

  const onExportReportClick = () => {
    dispatch(downloadReportAction({ reportType, filters: generateFiltersObject(), ids: selectedIds }));
  };

  const options = {
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    filter: false,
    filterType: 'dropdown',
    selectableRows: 'none',
  };

  const transformResponseData = () => {
    if (reportData?.items) {
      return reportData.items.map(item => Object.values(item));
    }

    return [];
  };

  if (!allowedRoles.includes(profile.role) && !isAdmin) {
    window.location.href = '/';
  }

  const regularWeekPicker = () => {
    return (
      <FilterItem>
        <UPWeekPicker
          value={week}
          weekValue={currentWeek}
          onChange={onWeekPickerChange}
          label={t(HEADER__WEEK)}
          className="datepicker--white-background"
        />
      </FilterItem>
    );
  };

  const referenceDatePicker = () => {
    return (
      <FilterItem>
        <UPDatepicker
          value={referenceDate}
          onChange={date => {
            setReferenceDate(date);
            handleFilterChange('selectedEmployees', []);
          }}
          label={t(DOCUMENTS__DATE)}
          required
          className="datepicker--white-background"
        />
      </FilterItem>
    );
  };

  const rangeWeekPicker = () => {
    return reportType === ReportType.FTE_CHECK ? (
      <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
        <Box sx={{ marginRight: '16px' }}>
          <UPWeekPicker
            value={weekRange[0]}
            weekValue={startWeek}
            onChange={onStartWeekPickerChange}
            label={t(GENERAL__FROM)}
            className="datepicker--white-background"
          />
        </Box>
        <Box>
          <UPWeekPicker
            value={weekRange[1]}
            weekValue={endWeek}
            onChange={onEndWeekPickerChange}
            label={t(GENERAL__UNTIL)}
            className="datepicker--white-background"
          />
        </Box>
      </Box>
    ) : (
      <Box ml="8px" width="300px">
        <UPDateRangePicker
          month={new Date().getMonth()}
          startDate={startDate}
          endDate={endDate}
          setDateRange={setDateRange}
        />
      </Box>
    );
  };

  return (
    <>
      <SectionContainer sx={{ paddingTop: '16px' }}>
        <Box display="flex" justifyContent="space-between">
          <Box>
            {reportType === ReportType.DOCUMENT_FOLLOW_UP && referenceDatePicker()}
            {reportType !== ReportType.DOCUMENT_FOLLOW_UP &&
              (isReportWithInterval(reportType) ? rangeWeekPicker() : regularWeekPicker())}
          </Box>
          <Box display="flex" justifyContent="center" alignItems="center">
            <Box>
              <UPDropdown
                items={getReportTypesDropdownValues(profile.role)}
                onChange={e => {
                  setReportType(e.target.value);
                  setValidationMessage('');
                  setDocumentFollowUpGenerated(false);
                  dispatch(clearReportsAction());
                  handleFilterChange('selectedEmployees', []);
                }}
                value={reportType}
                label=""
                disableUnderline
              />
            </Box>
            {reportType === ReportType.STATISTICS_PER_COST_CENTER && (
              <Tooltip title={t(COST_ESTIMATION__TOOLTIP)} placement="left" arrow>
                <InfoIcon />
              </Tooltip>
            )}
          </Box>
          <Box display="flex" gap="8px">
            {isReportWithInterval(reportType) && (
              <Box>
                <ExportReportButton
                  text={
                    <Box display="flex" alignItems="center">
                      <DownloadIcon fill="white" />
                    </Box>
                  }
                  onClick={onExportReportClick}
                  disabled={selectedIds.length === 0}
                />
              </Box>
            )}
            <Box>
              <UPButton
                text={t(GENERAL__SHOW)}
                onClick={() => renderData()}
                disabled={ReportType.STATISTICS_PER_COST_CENTER && (!startDate || !endDate)}
              />
            </Box>
          </Box>
        </Box>
        {(reportType === ReportType.WEEKLY_CONTRACT_COUNT || reportType === ReportType.GENERAL_STATISTICS) && (
          <BaseReportingFilters
            filters={reportingFilters}
            dropdownData={dropdownData}
            handleFilterChange={handleFilterChange}
          />
        )}
        {reportType === ReportType.GENERAL_STATISTICS && (
          <GeneralStatisticsFilters
            filters={reportingFilters}
            dropdownData={dropdownData}
            handleFilterChange={handleFilterChange}
          />
        )}
        {reportType === ReportType.CLIENT_PERFORMANCE && (
          <ClientPerformanceFilters
            filters={reportingFilters}
            dropdownData={dropdownData}
            handleFilterChange={handleFilterChange}
          />
        )}
        {reportType === ReportType.STATISTICS_PER_COST_CENTER && (
          <StatisticsPerCostCenterFilters
            filters={reportingFilters}
            dropdownData={dropdownData}
            handleFilterChange={handleFilterChange}
          />
        )}
        {reportType === ReportType.FTE_CHECK && (
          <FTECheckFilters
            filters={reportingFilters}
            dropdownData={dropdownData}
            handleFilterChange={handleFilterChange}
          />
        )}
        {reportType === ReportType.DOCUMENT_FOLLOW_UP && (
          <DocumentFollowUpFilters
            filters={reportingFilters}
            dropdownData={dropdownData}
            handleFilterChange={handleFilterChange}
            referenceDate={isValidReferenceDate ? formatDate(referenceDate) : ''}
          />
        )}
        {validationMessage && (
          <Box mt="8px">
            <ErrorBar message={validationMessage} />
          </Box>
        )}
        {reportError && (
          <Box mt="8px">
            <ErrorBar message={t(REPORTING__GENERATE_FAILED)} />
          </Box>
        )}
      </SectionContainer>
      <SectionContainer sx={{ paddingTop: '16px' }}>
        <Box sx={{ width: '100%' }}>
          {reportType === ReportType.WEEKLY_CONTRACT_COUNT && (
            <WeeklyContractCountReport
              items={mapDataToWeeklyReportData(reportData)}
              year={year}
              week={currentWeek}
              renderData={renderData}
            />
          )}
          {reportType === ReportType.GENERAL_STATISTICS && (
            <UPTable columns={reportData?.headerColumns} data={transformResponseData()} options={options} />
          )}
          {reportType === ReportType.REQUIRED_ACTIONS && (
            <RequiredActionsReport items={mapDataToRequiredActionsReportData(reportData)} />
          )}
          {reportType === ReportType.CLIENT_PERFORMANCE && (
            <ClientPerformanceReport items={mapDataToClientPerformanceReport(reportData)} />
          )}
          {reportType === ReportType.STATISTICS_PER_COST_CENTER && (
            <StatisticsPerCostCenterReport
              items={mapDataToStatisticsPerCostCenterReport(reportData)}
              setSelectedIds={setSelectedIds}
            />
          )}
          {reportType === ReportType.FTE_CHECK && (
            <FTECheckReport
              items={mapDataToFTECheckReport(reportData)}
              showCostCenterColumn={selectedCostCenters.length > 0}
              setSelectedIds={setSelectedIds}
            />
          )}
          {reportType === ReportType.DOCUMENT_FOLLOW_UP && (
            <DocumentFollowUpReport
              items={mapDataToDocumentFollowUpReport(reportData)}
              hasGenerated={documentFollowUpGenerated && !reportError}
            />
          )}
        </Box>
      </SectionContainer>
    </>
  );
};

export default Reporting;
